<template lang="pug">
  div
    v-banner(v-if="editedProducts.length > 0" sticky single-line color="success500")
      div.d-flex.justify-space-between
        p.mb-0.h6.white--text {{$t('save_changes')}} ({{ editedProducts.length }})
        div.d-flex.shrink.justify-end
          v-btn.button-AA-tiny.mr-1(outlined small height="24" color='basic100T08' :disabled="isLoading" @click="clearTask") {{$t('cancel')}}
          v-btn.button-AA-tiny.basic800--text(depressed small color="basic100" height="24" :loading="isLoading" @click="saveTask")  {{$t('save')}}
    div.pa-4
      v-form(ref="form")
        div.px-4.pt-2
          v-text-field.my-2(v-model="searchText" hide-details dense outlined background-color="basic200" height="40" :placeholder = "$t('search_product')")
            template(v-slot:append)
              eva-icon(name="search-outline" :fill="$vuetify.theme.themes[$theme].basic600")
        v-divider.my-4
        //- Apply all
        product-tiers-apply-all(ref="multiple" :companyTiers="companyTiers" :isFetching="isFetching" @updateData="applyAllTask")
        //- Or
        div.d-flex.align-center.pa-4
          v-divider
          div.basic600--text.text-uppercase.px-2.c2  {{$t('or')}}
          v-divider

        //- Individual header
        div.label.basic600--text.text-uppercase.px-4 {{$t('enter_individual_value')}}
        //- Loading state
        v-layout.py-6(v-if="isFetching")
          v-row.fill-height.ma-0(align='center' justify='center')
            v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
        //- Individual
        div.pa-4(v-else)
          //- Empty list or search not found
          template(v-if="productTiers.length === 0")
            div.py-6.d-flex.flex-column.align-center
              v-row.fill-height.ma-0(align='center' justify='center')
                div.mx-auto
                  v-img.mx-auto(:src="require('@/assets/images/icon_empty.png')" style="width:80px; height:80px" contain)
                  p.caption {{$t('products_not_found')}}
          template(v-else v-for="product in productTiers")
            product-tier-individual-item.scrolling-wrapper(ref="individuals" :parentProduct="product" :edited="editedProducts[editedProducts.findIndex(edited => edited.id===product.id)]" :focused="editedProducts.some(edited => edited.id===product.id)" @updateVariants="updateVariantsTask" @editValue="updateTask" @removeValue="removeTask")
            template(v-if="product.has_variants && product.variantExpanded" v-for="(variant, variantIndex) in product.variants")
              div.scrolling-wrapper(v-if="product.view")
                div.d-flex.align-center
                  div.shrink.mr-4
                    p.label.basic600--text {{ variantIndex + 1 }}
                  div.grow
                    product-tier-individual-item(ref="variantIndividuals" :parentProduct="variant" :edited="editedProducts[editedProducts.findIndex(edited => edited.id===variant.id)]" :focused="editedProducts.some(edited => edited.id===variant.id)" @updateVariants="updateVariantsTask" @editValue="updateTask" @removeValue="removeTask")
                v-divider.basic300
            v-divider.basic300
        y-pagination-custom.pa-4(v-if="count > 0 && !isFetching" v-model="page" :parentPageSize.sync="pageSize" :count="count" :text="'Show items'")
</template>

<script>
import clone from 'just-clone'
import { mapGetters, mapActions } from 'vuex'
import ProductTiersApplyAll from '@/components/resellers/productTiers/ProductTiersApplyAll.vue'
import ProductTierIndividualItem from '@/components/resellers/productTiers/ProductTierIndividualItem.vue'

export default {
  name: 'ProductTiers',
  components: {
    ProductTiersApplyAll,
    ProductTierIndividualItem
  },
  props: {
    companyTiers: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean
    },
    edited: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isFetching: false,
      fetchUrl: '/product_tiers/',
      productTiers: [],
      editedProducts: [],
      page: 1,
      pageSize: 20,
      count: 0,
      searchText: '',
      searchCountDown: null
    }
  },
  mounted() {
    this.page = this.$route.query.page ? parseInt(this.$route.query.page) : 1
    this.fetchProductTiers()
  },
  watch: {
    page(val) {
      history.pushState({}, null, this.$route.path + '?page=' + val)
      if (!this.isFetching) {
        this.fetchProductTiers()
      }
    },
    pageSize() {
      this.page = 1
      this.fetchProductTiers()
    },
    searchText: {
      handler() {
        if (this.searchCountDown) {
          clearTimeout(this.searchCountDown)
        }
        this.searchCountDown = setTimeout(() => {
          this.page = 1
          this.fetchProductTiers()
        }, 200)
      },
      deep: true
    },
    editedProducts(val) {
      this.$emit('update:edited', val.length)
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    filterParams() {
      const params = {
        company: this.company.id,
        ordering: '-id',
        page: this.page,
        page_size: this.pageSize,
        search: this.searchText
      }
      return params
    },
    showModal: {
      get() {
        return this.open
      },
      set(val) {
        return this.$emit('update:open', val)
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchProductTiers() {
      this.isFetching = true
      this.axios
        .get(this.fetchUrl, { params: this.filterParams })
        .then(resp => {
          this.productTiers = resp.data.results.map(product => {
            const data = { ...product }
            if (data.has_variants) {
              data.view = false
              data.variantExpanded = false
              data.tiers = null
            } else {
              data.tiers = this.mapTiers(product.tiers)
            }
            return data
          })
          this.count = resp.data.count
          this.isFetching = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    updateVariantsTask({ parentProduct, variants }) {
      this.productTiers.forEach(product => {
        if (product.id === parentProduct.id) {
          product.variants = variants.map(variant => {
            variant.tiers = this.mapTiers(variant.tiers)
            return variant
          })
          product.variantExpanded = true
        }
      })
    },
    mapTiers(productTiers) {
      return this.companyTiers.map(tier => {
        const commonData = {
          id: tier.id,
          name: tier.name,
          commission_type: tier.commission_type,
          commission_value: null
        }
        if (productTiers.length === 0) {
          return commonData
        }
        const found = productTiers.find(productTier => productTier.id === tier.id)
        if (found) {
          return {
            ...commonData,
            commission_value: found.commission_type === 'amount' ? found.commission_value / this.$currency.multiplier : found.commission_value
          }
        }
        return commonData
      })
    },
    removeTask(product) {
      const index = this.editedProducts.findIndex(edited => edited.id === product.id)
      if (index !== -1) {
        this.editedProducts.splice(index, 1)
      }
    },
    updateTask(product) {
      const index = this.editedProducts.findIndex(edited => edited.id === product.id)
      if (index === -1) {
        this.editedProducts.push(product)
      } else {
        this.editedProducts[index].tiers = product.tiers
      }
    },
    async applyAllTask({ tier, value }) {
      this.$emit('update:isLoading', true)
      function updateTier(tiers) {
        return tiers.map(currentTier => {
          if (currentTier.id === tier) {
            currentTier.commission_value = value
          }
          return currentTier
        })
      }
      function mappingValues(groups, editedProducts) {
        if (!groups) {
          return
        }
        groups.forEach(item => {
          item.tiers = updateTier(item.tiers)
          const index = editedProducts.findIndex(edited => edited.id === item.parentProduct.id)
          if (index === -1) {
            const newData = {
              id: item.parentProduct.id,
              tiers: clone(item.parentProduct.tiers)
            }
            newData.tiers = updateTier(newData.tiers)
            editedProducts.push(newData)
          } else {
            editedProducts[index].tiers = updateTier(editedProducts[index].tiers)
          }
        })
      }
      // Get both groups
      const singleProducts = this.$refs.individuals.filter(individual => individual.tiers)
      const variantProducts = this.$refs.individuals.filter(individual => !individual.tiers)
      // Mapping single products values
      mappingValues(singleProducts, this.editedProducts)
      // Expand all variants first
      for (const variant of variantProducts) {
        if (!variant.parentProduct.variantExpanded) {
          await variant.expandVariants()
        }
      }
      // Mapping variant products values
      mappingValues(this.$refs.variantIndividuals, this.editedProducts)
      this.$emit('update:isLoading', false)
    },
    clearTask() {
      this.editedProducts = []
      this.productTiers = []
      this.fetchProductTiers()
    },
    saveTask() {
      this.$emit('update:isLoading', true)
      // Prepare url, data
      const url = `/product_tiers?company=${this.company.id}`
      const data = { products: this.editedProducts }
      data.products.forEach(product => {
        if (product.tiers.every(tier => !tier.commission_value)) {
          product.tiers = []
          return
        }
        product.tiers = product.tiers.map(tier => {
          const newData = {
            ...tier,
            commission_value:
              tier.commission_type === 'amount'
                ? parseFloat(tier.commission_value)
                    .toFixed(this.$currency.exponent)
                    .replace('.', '')
                : tier.commission_value
          }
          return this.$options.filters.filterDisallowedObjectProperties(newData, ['commission_type', 'name'])
        })
      })
      // Post request
      this.$axios
        .post(url, data)
        .then(() => {
          this.clearTask()
          this.showMessage(['success', this.$t('success_message.product_commissions_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.$emit('update:isLoading', false)
        })
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<style lang="scss" scoped>
.scrolling-wrapper {
  overflow-x: scroll;
  overflow-y: hidden;
  white-space: nowrap;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
